/**
 * syncPull — PULL incremental puro y stateless (compartido por la app)
 *
 * ÚNICA fuente de la lógica "fetch de Supabase los cambios desde `updated_at`
 * local + bulkPutSilent de TODO (vivos y soft-deletes)". Antes había
 * TRES copias de esta lógica (Path A en SyncManager init/periódico, Path B en
 * TableRealtimeSync realtime, Path C en company-switch) con bugs sutiles en las
 * legacy (cursor global en vez de por empresa). Esta es la versión canónica:
 * la misma que usaba Path B (la correcta), extraída a un módulo puro.
 *
 * Por qué es puro/stateless: `syncService.init()` corre ANTES que
 * `initRealtime()`, así que en `init()` → `pullAllTables(true)` la instancia de
 * `TableRealtimeSync` todavía no existe. Un módulo stateless que recibe el
 * cliente Supabase por parámetro permite PULLear sin depender de esa instancia.
 *
 * No toca `window` ni lógica de UI; solo `getRepositories`/`isFactoryReady`
 * (seguros en web). El Service Worker NO lo importa (no hace PULL, solo PUSH),
 * pero cumple el mismo patrón de pureza que syncBatcher/syncLock/syncFields.
 *
 * Ver: docs/SYNC_BATCHING_SW_APP.md (patrón de módulos puros compartidos).
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { getRepositories, isFactoryReady } from '../services/storage';
import type { PendingChange } from '../configs/tableConfig';

/**
 * Configuración de una tabla para el PULL incremental.
 *
 * Es el mismo shape que tenía `RealtimeTableConfig` en TableRealtimeSync (Path B);
 * TableRealtimeSync lo reexporta como alias para no romper imports.
 */
export interface PullTableConfig {
  /** Nombre de la tabla en Supabase (ej: 'gastos', 'animales'). */
  table: string;
  /**
   * Key del repositorio en `getRepositories()` (ej: 'gasto', 'animal').
   * Requerido salvo que se provea un `repo` inline.
   */
  repoKey?: string;
  /**
   * Repo inline: alternativa a `repoKey` para tablas que no tienen repositorio
   * registrado en la factory (ej: tablas de relación con PK compuesta de la
   * taxonomía de conceptos, expuestas como sub-repos del ConceptoRepository).
   */
  repo?: PullRepository;
  /** Campo PK usado para borrar (default: 'id'). Animales → 'caravana'. */
  pkField?: string;
  /**
   * Si la tabla maneja soft-delete con `is_deleted` en Supabase (default: true).
   * NO controla si se borra o no localmente: el PULL SIEMPRE upsertea todo
   * (vivos y soft-deletes), y las lecturas (`getAll`/queries) filtran
   * `is_deleted = false`. Este flag solo se usa para telemetría (contar
   * cuántas filas traídas eran soft-delete).
   */
  isDeletedLogic?: boolean;
  /** Columna usada como cursor incremental (default: 'updated_at'). */
  dateColumn?: string;
  /**
   * Campo por el que se scopea por empresa en el catch-up (default:
   * 'empresa_id'). Para tablas donde el PK es el propio empresa_id (ej:
   * `empresas`, que no tiene `empresa_id`), usar 'id' → el PULL filtra por
   * `id = empresaId` y el cursor es global (getLastByDate).
   *
   * `null` = SIN filtro de empresa: para tablas con filas globales
   * (empresa_id NULL) + propias, donde el RLS del SELECT ya devuelve
   * exactamente lo visible (ej: `categorias`/`conceptos` de la taxonomía).
   */
  scopeField?: string | null;
  /**
   * Predicado de scope para el RECONCILE del fullSnapshot en tablas con
   * `scopeField: null` que mezclan filas globales (empresa_id NULL) + propias
   * (ej: `categorias`/`conceptos` de la taxonomía). Recibe la fila local y el
   * `empresaId` activo, y decide si la fila PUEDE ser candidata a borrado:
   * una fila que no pasa el filtro (de OTRA empresa) no representa ausencia
   * remota — el snapshot de esta empresa jamás la devolvería — así que el
   * reconcile debe dejarla intacta en el caché.
   *
   * Si el predicado lanza, se trata como fuera de scope (protector: ante la
   * duda, no borrar). Sin scopeFilter se aplica el guard previo por
   * `scopeField` (tablas empresa-scoped).
   */
  scopeFilter?: (row: any, empresaId: string) => boolean;
  /**
   * PULL por FULL SNAPSHOT en lugar de incremental (default: false): trae TODAS
   * las filas visibles del scope y RECONCILEA los hard-deletes remotos (las
   * filas locales ausentes en el server se borran localmente, en silencio).
   *
   * Para tablas chicas donde el cursor incremental no aplica o no alcanza:
   * sin `updated_at` (ej: `conceptos_ocultos`) o con hard-deletes que un pull
   * por cursor nunca vería.
   */
  fullSnapshot?: boolean;
  /**
   * Registrar esta tabla en realtime (default: true). `false` para tablas SIN
   * trigger de broadcast en el canal `empresa:{id}`: si se registraran,
   * `isCoveredByRealtime()` daría true y el PULL periódico las saltearía
   * creyéndolas cubiertas por realtime que nunca llega.
   */
  realtime?: boolean;
  /**
   * Acceso a la cola de cambios pendientes de push (offline). Lo cablea el
   * SyncManager desde su platform adapter. Se usa SOLO en el reconcile del
   * fullSnapshot: las filas locales con INSERT/UPDATE pendiente aún no están
   * en el server, así que el snapshot no las devuelve — sin esta protección el
   * reconcile las borraría del caché y el usuario vería desaparecer lo que
   * creó offline hasta el próximo ciclo (y recrearla duplicaría la PK en el
   * push).
   */
  getPendingChanges?: () => Promise<PendingChange[]>;
  /**
   * Hook opcional tras aplicar el PULL de esta tabla (ej: reconstruir
   * `stocks` derivada cuando la tabla es `stocks_historico`). Se invoca solo
   * si el server devolvió cambios (data.length > 0).
   */
  afterCatchUp?: () => Promise<void>;
}

/**
 * Repositorio mínimo que este módulo consume. `getRepositories()` ya devuelve
 * objetos que cumplen esta forma (vía BaseRepository / adapters).
 */
interface PullRepository {
  bulkPutSilent(items: any[]): Promise<void>;
  getLastByDate(dateColumn?: string): Promise<any>;
  getLastByDateForEmpresa?(empresaId: string, dateColumn?: string): Promise<any>;
  /** Opcionales, solo para fullSnapshot (reconcile de hard-deletes remotos). */
  getAll?(): Promise<any[]>;
  bulkDeleteSilent?(ids: any[]): Promise<void>;
}

/** Cursor inicial cuando no hay datos locales (trae todo via .gt). */
export const EPOCH = new Date(0).toISOString();

/**
 * ¿Tabla empresa-scoped sin empresa activa?
 *
 * Carrera del primer login: AuthContext escribe el profile local de forma
 * async (sin await) después de setUser, así que sync.init() puede llegar
 * antes y `getActiveEmpresaId()` devolver ''. Consultar con
 * `.eq(scopeField, '')` manda un uuid vacío a PostgREST → 400 `22P02:
 * invalid input syntax for type uuid: ""` en cada tabla. Con scope vacío no
 * hay nada que pullear: se omite la tabla (SyncManager espera al profile;
 * realtime y el sync periódico hacen el catch-up cuando aterriza).
 */
function isScopedSinEmpresa(cfg: PullTableConfig, empresaId: string): boolean {
  // scopeField undefined → default 'empresa_id' (scoped); null → sin scope (RLS)
  return cfg.scopeField !== null && !empresaId;
}

/**
 * Obtiene el repositorio para un repoKey. Retorna null si la factory de
 * repositorios aún no está lista (arranque), la key no existe o es undefined
 * (configs con repo inline).
 */
export function getPullRepo(repoKey: string | undefined): PullRepository | null {
  try {
    if (!isFactoryReady() || !repoKey) return null;
    const repo = (getRepositories() as any)[repoKey];
    return (repo ?? null) as PullRepository | null;
  } catch {
    return null;
  }
}

/**
 * Cursor local (`updated_at` más reciente) para una tabla, FILTRADO POR EMPRESA.
 *
 * Sin el filtro por empresa, al cambiar de empresa el cursor global sería el
 * `updated_at` de otra empresa en caché y el `.gt('updated_at')` no traería los
 * registros de la empresa nueva con `updated_at` anterior (mismo bug que tenía
 * el Path A legacy). Por eso para tablas empresa-scoped se usa
 * `getLastByDateForEmpresa`.
 *
 * - `scopeField === 'empresa_id'` + empresaId → `getLastByDateForEmpresa`
 * - cualquier otro caso (ej: `empresas` con scopeField='id', o sin empresaId)
 *   → `getLastByDate` global, o EPOCH si no hay nada.
 */
export async function getLocalCursor(
  cfg: PullTableConfig,
  empresaId: string | null,
): Promise<string> {
  const dateColumn = cfg.dateColumn ?? 'updated_at';
  const scopeField = cfg.scopeField ?? 'empresa_id';
  try {
    if (!empresaId) return EPOCH;
    const repo = cfg.repo ?? getPullRepo(cfg.repoKey);
    if (!repo) return EPOCH;

    // Para tablas con scopeField='empresa_id' usamos el cursor filtrado por
    // empresa. Para tablas cuyo scope es por id (ej: empresas, una sola fila
    // por empresa en caché) o sin scope (scopeField=null: el caché contiene
    // todo lo visible vía RLS) usamos el cursor global.
    const useScoped = cfg.scopeField !== null && scopeField === 'empresa_id';
    const last = (useScoped && repo.getLastByDateForEmpresa)
      ? await repo.getLastByDateForEmpresa(empresaId, dateColumn)
      : await repo.getLastByDate(dateColumn);
    return (last as any)?.[dateColumn] || EPOCH;
  } catch (error) {
    console.error(`[syncPull] Error obteniendo cursor de ${cfg.table}:`, error);
    return EPOCH;
  }
}

/**
 * Trae los registros modificados desde `updatedAt` y los aplica al caché local.
 *
 * UPSERT de TODO lo que devuelve el server, incluidos los soft-deletes
 * (`is_deleted = true`): el registro queda en el caché local marcado, y son las
 * lecturas (`getAll`/queries de los repositorios) las que filtran
 * `is_deleted = false`. No se hard-deletea nada acá. Esto da consistencia
 * offline y permite mostrar/restaurar eliminados.
 *
 * @returns { upserted, softDeleted } contadores (para logging/telemetría).
 */
export async function pullTableSince(
  supabase: SupabaseClient,
  cfg: PullTableConfig,
  empresaId: string,
  updatedAt: string,
): Promise<{ upserted: number; softDeleted: number }> {
  if (isScopedSinEmpresa(cfg, empresaId)) {
    console.warn(`[syncPull] ${cfg.table}: empresaId vacío (profile sin cargar), PULL omitido`);
    return { upserted: 0, softDeleted: 0 };
  }
  if (cfg.fullSnapshot) {
    return pullTableSnapshot(supabase, cfg, empresaId);
  }

  const dateColumn = cfg.dateColumn ?? 'updated_at';
  const isDeletedLogic = cfg.isDeletedLogic ?? true;
  const scopeField = cfg.scopeField ?? 'empresa_id';

  try {
    let query = supabase
      .from(cfg.table)
      .select('*');
    // Endurecer scope por empresa además del RLS (scopeField=null → sin
    // filtro: el RLS ya devuelve exactamente lo visible). Para empresas
    // (scopeField='id') filtra por id = empresaId (la empresa activa).
    if (cfg.scopeField !== null) {
      query = query.eq(scopeField, empresaId);
    }
    const { data, error } = await query
      .gt(dateColumn, updatedAt)
      .order(dateColumn, { ascending: true });

    if (error) {
      console.error(`[syncPull] Error cargando ${cfg.table}:`, error);
      return { upserted: 0, softDeleted: 0 };
    }
    if (!data || data.length === 0) {
      console.log(`[syncPull] No hay ${cfg.table} actualizados desde`, updatedAt);
      return { upserted: 0, softDeleted: 0 };
    }

    const repo = getPullRepo(cfg.repoKey);
    if (!repo) {
      console.log(`[syncPull] Repo '${cfg.repoKey}' no disponible`);
      return { upserted: 0, softDeleted: 0 };
    }

    // Upsert de vivos Y soft-deletes. Los soft-deletes quedan en caché con
    // is_deleted=true y las lecturas los excluyen. bulkPutSilent emite solo el
    // evento local (refresca UI) sin re-emitir sync.
    await repo.bulkPutSilent(data);
    const softDeleted = isDeletedLogic
      ? data.filter((r: any) => r.is_deleted).length
      : 0;

    // Hook post-catch-up (ej: reconstruir stocks derivada). Solo si el server
    // devolvió cambios (paridad con el comportamiento previo de Path B).
    if (cfg.afterCatchUp) {
      try { await cfg.afterCatchUp(); } catch (e) {
        console.error(`[syncPull] Error en afterCatchUp de ${cfg.table}:`, e);
      }
    }

    console.log(
      `[syncPull] ✅ ${cfg.table}: ${data.length} upsert (${softDeleted} soft-delete) desde ${updatedAt}`
    );
    return { upserted: data.length, softDeleted };
  } catch (error) {
    console.error(`[syncPull] Error en pullTableSince(${cfg.table}):`, error);
    return { upserted: 0, softDeleted: 0 };
  }
}

/**
 * PULL incremental de una tabla: calcula el cursor local y trae los cambios.
 * Cubre automáticamente el caso "caché vacío para esta empresa" (cursor EPOCH
 * → trae todo), así que sirve como carga inicial y como catch-up.
 */
export async function pullTableIncremental(
  supabase: SupabaseClient,
  cfg: PullTableConfig,
  empresaId: string,
): Promise<void> {
  if (cfg.fullSnapshot) {
    // Sin cursor: el snapshot trae todo el scope siempre.
    await pullTableSnapshot(supabase, cfg, empresaId);
    return;
  }
  const cursor = await getLocalCursor(cfg, empresaId);
  await pullTableSince(supabase, cfg, empresaId, cursor);
}

/**
 * PULL por FULL SNAPSHOT: trae TODAS las filas visibles del scope (sin cursor
 * incremental) y las aplica al caché local.
 *
 * Además del upsert (bulkPutSilent), RECONCILEA hard-deletes remotos: las
 * filas locales del scope que el server ya no devuelve se borran localmente
 * con bulkDeleteSilent (evento local únicamente — no se re-pushea nada). Esto
 * cubre casos que el pull por cursor no puede ver: filas borradas remotamente
 * (p.ej. un `desocultar` desde otro dispositivo) nunca "actualizan" su
 * updated_at porque directamente dejan de existir.
 *
 * El identify de cada fila para el reconcile es `pkField` (default 'id'); si
 * la fila no lo tiene (tablas de relación con PK compuesta), se usa la tupla
 * [empresa_id, concepto_id].
 *
 * Nota: una fila local con INSERT/UPDATE pendiente de push (creada offline) no
 * está en el snapshot. Si el config cablea `getPendingChanges`, el reconcile la
 * PROTEGE (no la borra) hasta que el push la cree remota. Sin acceso a la cola
 * (config sin cablear o lectura fallida) se aplica el comportamiento previo:
 * se borra del caché, la cola de pendientes sobrevive, el push la recrea remota
 * y el próximo snapshot la trae de vuelta.
 */
async function pullTableSnapshot(
  supabase: SupabaseClient,
  cfg: PullTableConfig,
  empresaId: string,
): Promise<{ upserted: number; softDeleted: number }> {
  const scopeField = cfg.scopeField ?? 'empresa_id';
  const pkField = cfg.pkField ?? 'id';

  if (isScopedSinEmpresa(cfg, empresaId)) {
    console.warn(`[syncPull] ${cfg.table}: empresaId vacío (profile sin cargar), snapshot omitido`);
    return { upserted: 0, softDeleted: 0 };
  }

  try {
    let query = supabase.from(cfg.table).select('*');
    if (cfg.scopeField !== null) {
      query = query.eq(scopeField, empresaId);
    }
    const { data, error } = await query;

    if (error) {
      console.error(`[syncPull] Error cargando snapshot de ${cfg.table}:`, error);
      return { upserted: 0, softDeleted: 0 };
    }
    const rows = data ?? [];

    const repo = cfg.repo ?? getPullRepo(cfg.repoKey);
    if (!repo) {
      console.log(`[syncPull] Repo de '${cfg.repoKey ?? '(inline)'}' no disponible para snapshot de ${cfg.table}`);
      return { upserted: 0, softDeleted: 0 };
    }

    await repo.bulkPutSilent(rows);

    // Reconcile de hard-deletes remotos (ver docstring).
    let reconciled = 0;
    if (typeof repo.getAll === 'function' && typeof repo.bulkDeleteSilent === 'function') {
      const keyOf = (row: any): string | (string | null)[] =>
        row[pkField] ?? [row.empresa_id, row.concepto_id];
      const serverKeys = new Set(rows.map((r: any) => JSON.stringify(keyOf(r))));

      // Protección de pendientes: filas locales cuyo INSERT/UPDATE sigue en la
      // cola offline no están en el snapshot (nunca llegaron al server) → el
      // reconcile no debe borrarlas. El push las creará y el próximo snapshot
      // las confirmará. Si no se puede leer la cola, se sigue sin protección
      // (mejor un reconcile incompleto que no pullear la tabla).
      let pendingKeys = new Set<string>();
      if (cfg.getPendingChanges) {
        try {
          const pendientes = await cfg.getPendingChanges();
          pendingKeys = new Set(
            pendientes
              .filter(p => p.table === cfg.table && p.action !== 'DELETE' && p.action !== 'BULK_DELETE')
              .flatMap(p => (Array.isArray(p.record) ? p.record : [p.record]) as unknown[])
              .filter((r: any): r is Record<string, unknown> => !!r && typeof r === 'object')
              .map(r => JSON.stringify(keyOf(r)))
          );
        } catch (e) {
          console.warn(`[syncPull] No se pudieron leer pendientes de ${cfg.table}; reconcile sin protección`, e);
        }
      }

      const locales = await repo.getAll();
      // Scope del reconcile: qué filas locales PUEDEN ser candidatas a borrado.
      // Con scopeFilter (tablas globales+propias, ej: taxonomía) decide el
      // predicado; si lanza, fuera de scope (protector: ante la duda, no
      // borrar). Sin scopeFilter rige el guard previo por scopeField.
      const inScope = (l: any): boolean => {
        if (cfg.scopeFilter) {
          try {
            return cfg.scopeFilter(l, empresaId);
          } catch (e) {
            console.warn(`[syncPull] scopeFilter de ${cfg.table} lanzó; fila protegida`, e);
            return false;
          }
        }
        // Tablas empresa-scoped: las filas de OTRAS empresas no representan
        // ausencia remota (no forman parte de este snapshot) → intocables.
        if (cfg.scopeField !== null && scopeField === 'empresa_id' && l.empresa_id !== empresaId) {
          return false;
        }
        return true;
      };
      const stale = locales.filter((l: any) => {
        if (!inScope(l)) {
          return false; // fuera del scope de este snapshot → intocable
        }
        if (pendingKeys.has(JSON.stringify(keyOf(l)))) {
          return false; // INSERT/UPDATE pendiente de push: queda hasta que llegue al server
        }
        return !serverKeys.has(JSON.stringify(keyOf(l)));
      });
      if (stale.length > 0) {
        await repo.bulkDeleteSilent(stale.map(keyOf));
        reconciled = stale.length;
      }
    }

    if (rows.length > 0 || reconciled > 0) {
      console.log(`[syncPull] ✅ ${cfg.table} (snapshot): ${rows.length} upsert, ${reconciled} locales reconciliados`);
    }
    return { upserted: rows.length, softDeleted: 0 };
  } catch (error) {
    console.error(`[syncPull] Error en pullTableSnapshot(${cfg.table}):`, error);
    return { upserted: 0, softDeleted: 0 };
  }
}

/**
 * PULL incremental de un conjunto de tablas. Si se pasa `tables`, filtra los
 * configs a esas tablas; si no, pullea todas las de `configs`.
 *
 * Es el entry point que usan `SyncManager.pullAllTables` (init/periódico) y
 * `SyncManager.reloadAfterCompanySwitch` (company-switch).
 */
export async function pullTables(
  supabase: SupabaseClient,
  configs: PullTableConfig[],
  empresaId: string,
  tables?: string[],
): Promise<void> {
  const filter = tables ? new Set(tables) : null;
  const omitidasSinEmpresa: string[] = [];
  for (const cfg of configs) {
    if (filter && !filter.has(cfg.table)) continue;
    // Guard de la carrera del primer login (ver isScopedSinEmpresa): filtrar
    // acá arriba evita un warn por tabla y hasta armar la query.
    if (isScopedSinEmpresa(cfg, empresaId)) {
      omitidasSinEmpresa.push(cfg.table);
      continue;
    }
    await pullTableIncremental(supabase, cfg, empresaId);
  }
  if (omitidasSinEmpresa.length > 0) {
    console.warn(
      `[syncPull] empresaId vacío (profile sin cargar): PULL omitido para [${omitidasSinEmpresa.join(', ')}]`
    );
  }
}
